import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../core/winpresentation/SimplePresentationRunner";
import {HTH} from "../HTH";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/utils/server/IData";
import {gsap} from "gsap";
import {HTHWinConfig} from "../configs/HTHWinConfig";
import {HTHHelper} from "../utils/HTHHelper";
import {HTHSymbol, HTHSymbolStates} from "../symbols/HTHSymbol";
import {HTHXNudgeSymbol} from "../symbols/HTHXNudgeSymbol";
import {HTHSafeSymbol} from "../symbols/HTHSafeSymbol";
import {HTHEnhancerSymbol} from "../symbols/HTHEnhancerSymbol";
import {HTHSound} from "../sound/HTHSound";

export class HTHInitialWinPresentation implements SimplePresentation {
    constructor() {
    }

    shouldRun(): boolean {
        return HTH.model.isNextAvalanche && HTH.model.gameData.betWayWins.length > 0 && !HTH.model.isGodModeMaxWin;
    }

    start(controller: SimplePresentationRunnerController): void {
        const initialWinTl: gsap.core.Timeline = gsap.timeline({
                onComplete: () => {
                    controller.setSkipCallback();
                    controller.finish();
                }
            }),
            isLargerWin: boolean = HTH.model.currentWinRatio >= HTHWinConfig.NORMAL_WIN_RATIOS.normalWin[3].ratio,
            delayBetweenReelHighlight: number = (isLargerWin ? 0.05 : 0),
            delayBetweenSymbolGroup: number = (isLargerWin ? 0.2 : 0);

        initialWinTl.add(() => {
            if (!HTHHelper.isWinBelowStake()) {
                if (HTH.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
                    HTHSound.playTrack(HTHSound.INDIVIDUAL_WIN)?.setRate(1.4);
                } else {
                    HTHSound.playTrack(HTHSound.INDIVIDUAL_WIN)?.setRate(1);
                }
            }
        }, 0);

        controller.setSkipCallback(() => {
            if (HTHSound.soundPlayer.fetchSoundPlayer(HTHSound.INDIVIDUAL_WIN)?.isPlaying) {
                HTHSound.soundPlayer.fetchSoundPlayer(HTHSound.INDIVIDUAL_WIN)?.setRate(2);
            }
            initialWinTl.timeScale(2);
        });
        initialWinTl.add(HTH.reelArea.setNoWinSprite(), 0);


        //highlight and show individual amount plus ways
        const individualHighlightTl: gsap.core.Timeline = gsap.timeline();
        HTH.model.gameData.betWayWins.forEach((oneSymWin: IBetWayWinData, index: number) => {
            const oneHighlightTl: gsap.core.Timeline = gsap.timeline(),
                winNumPlayPosition: number[] = HTH.individualWinTextView.findLastNoWildSymbolPosition(oneSymWin);
            oneSymWin.reelWinPositions.forEach((reelWin: boolean[], i: number) => {
                reelWin.forEach((winPos: boolean, j: number) => {
                    if (winPos) {
                        const sym: HTHSymbol = HTH.reelArea.getSymbol([i, j]);

                        if (i === winNumPlayPosition[0] && j === winNumPlayPosition[1]) {
                            oneHighlightTl.add(() => {
                                HTH.individualWinTextView.playWinNumbers(oneSymWin, false, winNumPlayPosition)
                            }, 0);
                        }

                        if (sym.symName !== "EB" && sym.symName !== "SA") {
                            sym.playInitialWin();
                        }
                        oneHighlightTl.add(() => {
                            sym.symbolState = HTHSymbolStates.WIN_HIGHLIGHT;
                        }, 0);
                        oneHighlightTl.add(sym.highlight(), 0);
                    }
                });
            });
            individualHighlightTl.add(oneHighlightTl, index * delayBetweenSymbolGroup);
        });
        initialWinTl.add(individualHighlightTl);


        // explode winning symbol left to right
        const explodeTl: gsap.core.Timeline = gsap.timeline();
        let countupForEBSound: number = 0;
        let countupForSASound: number = 0;
        const perReelExplosionSoundPlayed: boolean[] = [false, false, false, false, false, false];
        HTH.model.gameData.betWayWins.forEach((oneSymGroup: IBetWayWinData, index: number) => {
            const oneExplodeTl: gsap.core.Timeline = gsap.timeline();
            oneSymGroup.reelWinPositions.forEach((reelWin: boolean[], i: number) => {
                reelWin.forEach((winPos: boolean, j: number) => {
                    if (winPos) {
                        const sym: HTHSymbol = HTH.reelArea.getSymbol([i, j]);
                        if (!perReelExplosionSoundPlayed[i] && isLargerWin) {
                            perReelExplosionSoundPlayed[i] = true;
                            oneExplodeTl.add(() => {
                                HTHSound.playTrack(HTHSound.INDIVIDUAL_WIN_REEL_EXPLODE[JSON.parse(JSON.stringify(i))])?.setRate(1);
                            }, i * delayBetweenReelHighlight);
                        }
                        if (sym.symName === "XN" || sym.symName === "SA" || sym.symName === "EB") {
                            if (!((<HTHXNudgeSymbol | HTHSafeSymbol | HTHEnhancerSymbol>sym).participatedInWin)) {
                                (<HTHXNudgeSymbol | HTHSafeSymbol | HTHEnhancerSymbol>sym).participatedInWin = true;
                                oneExplodeTl.add(sym.explode(), i * delayBetweenReelHighlight);
                                if (sym.symName === "EB") {
                                    countupForEBSound++;
                                    if (countupForEBSound === 1) {
                                        oneExplodeTl.add(() => {
                                            HTHSound.playTrack(HTHSound.ENHANCER_CELL_DOOR_CLOSE);
                                        });
                                    }
                                }
                                if (sym.symName === "SA") {
                                    countupForSASound++;
                                    if (countupForSASound === 1) {
                                        oneExplodeTl.add(() => {
                                            HTHSound.playTrack(HTHSound.SAFE_CLOSE);
                                        });
                                    }
                                }
                            }
                        } else {
                            oneExplodeTl.add(sym.explode(), i * delayBetweenReelHighlight);
                        }
                    }
                });
            });
            explodeTl.add(oneExplodeTl, 0);
        });
        if (!isLargerWin) {
            explodeTl.add(() => {
                HTHSound.playRandomWinExplode(1);
            }, 0);
        }

        initialWinTl.add(explodeTl, "-=0.3");
        initialWinTl.addLabel("removeNoWin");


        initialWinTl.add(HTH.reelArea.unSetNoWinSprite(), "removeNoWin");

        if (!HTH.model.isBigWin && !HTH.model.brokeTheBank) {
            initialWinTl.add(() => {
                HTH.keypadPlugin.setWin(HTH.model.gameData.accumulatedRoundWin, true, HTHHelper.isWinBelowStake());
            });
        }

        if (HTH.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
            initialWinTl.timeScale(1.4);
        }

    }
}